import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  ColumnDef,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import {
  useConnectors,
  useTriggerExtraction,
  useDeleteConnector,
  useHealthCheck,
} from '@/hooks/useConnectors';
import { useConnectorLibraryState, type SortColumn } from '@/hooks/useConnectorLibraryState';
import { useConnectorLibraryPageSize } from '@/hooks/useConnectorLibraryPageSize';
import { useToast } from '@/hooks/useToast';
import type { Connector, ConnectorStatus, ConnectorExecution } from '@/types/connectors';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { ConnectorSearchInput } from './ConnectorSearchInput';
import { ConnectorFilters } from './ConnectorFilters';
import { AggregateHealthBar } from './AggregateHealthBar';
import { EmptyLibraryState } from './EmptyLibraryState';
import { PlatformLogo } from './PlatformLogo';
import { LastRunCell } from './LastRunCell';
import { NextRunCell } from './NextRunCell';
import { ActiveFilterChips } from './ActiveFilterChips';
import { RowActionsMenu } from './RowActionsMenu';
import { highlightMatch } from './searchHighlight';
import { getPlatformMeta } from './platformLogos';
import { Checkbox } from '@/components/ui/checkbox';
import { useConnectorSelection } from '@/hooks/useConnectorSelection';
import { useBulkConnectorActions } from '@/hooks/useBulkConnectorActions';
import { BulkActionBar } from './BulkActionBar';
import { BulkConfirmDialog } from './BulkConfirmDialog';
import { BulkDeleteDialog } from './BulkDeleteDialog';
import { BulkRowStatusBadge } from './BulkRowStatusBadge';

export interface ConnectorTableProps {
  onOpenConnector: (connector: Connector) => void;
  onAddConnector?: () => void;
}

const STATUS_PILL: Record<string, { className: string; label: string }> = {
  connected: { className: 'bg-success/10 text-success border-success/30', label: 'Connected' },
  running: { className: 'bg-warning/10 text-warning border-warning/30', label: 'Running' },
  error: { className: 'bg-destructive/10 text-destructive border-destructive/30', label: 'Error' },
  disabled: { className: 'bg-muted text-muted-foreground', label: 'Disabled' },
};

function lastRunSortKey(c: Connector): number {
  const exec = c.latest_execution as ConnectorExecution | undefined;
  if (!exec) return 0;
  const iso = exec.completed_at ?? exec.started_at;
  return iso ? new Date(iso).getTime() : 0;
}

/**
 * Stable, collision-free row identity (#970).
 *
 * Prefer the immutable config `id`; fall back to the `(connectorType, name)`
 * pair — which is DB-unique (`uq_connector_type_name`) — NOT the bare
 * `connectorType` slug. The old `c.id ?? c.connectorType` collapsed every
 * same-type row to the same key when `id` was absent, and pairing it with the
 * `name || connectorType` fallback made two Google Sheets rows route to the
 * same detail page. Keying on `id`, then `type:name`, keeps rows distinct.
 */
function rowKey(c: Connector): string {
  return String(c.id ?? `${c.connectorType}:${c.name ?? ''}`);
}

/** A connector whose name is missing/blank — surfaced rather than silently
 * collapsed to the type slug (#970). */
function isUnnamed(c: Connector): boolean {
  return !c.name?.trim();
}

export const ConnectorTable: React.FC<ConnectorTableProps> = ({
  onOpenConnector,
  onAddConnector,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useConnectors() as any;
  const connectors: Connector[] = data?.connectors ?? [];

  const lib = useConnectorLibraryState();
  const [pageSize, setPageSize] = useConnectorLibraryPageSize();

  const triggerExtraction = useTriggerExtraction() as any;
  const deleteConnector = useDeleteConnector();
  const healthCheck = useHealthCheck() as any;
  const [pendingId, setPendingId] = useState<string | number | null>(null);

  // Filtered rows. Sorting + pagination now live in DataTable / TanStack.
  const filtered = useMemo<Connector[]>(() => {
    const q = lib.q.trim().toLowerCase();
    let rows = connectors;
    if (q) {
      rows = rows.filter((c) => {
        const name = c.name.toLowerCase();
        const platform = getPlatformMeta(c.connectorType).displayName.toLowerCase();
        const description = (c.description ?? '').toLowerCase();
        // Exact ID match ("42" or "id 42"): a substring match would drag in every id containing the digits.
        const idMatch = c.id != null && q.replace(/^id\s*/, '') === String(c.id);
        return name.includes(q) || platform.includes(q) || description.includes(q) || idMatch;
      });
    }
    if (lib.platforms.length) {
      rows = rows.filter((c) => lib.platforms.includes(c.connectorType));
    }
    if (lib.statuses.length) {
      rows = rows.filter((c) =>
        lib.statuses.includes((c.status ?? 'disabled') as ConnectorStatus)
      );
    }
    return rows;
  }, [connectors, lib.q, lib.platforms, lib.statuses]);

  const selection = useConnectorSelection(filtered);
  const bulk = useBulkConnectorActions();
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  React.useEffect(() => {
    if (selection.count === 0) return;
    if (confirmDisable || confirmDelete) return;   // Let the dialog handle Esc itself
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        selection.clear();
        bulk.clearStatus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selection.count, selection.clear, bulk.clearStatus, selection, bulk, confirmDisable, confirmDelete]);

  const platformCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const c of connectors) out[c.connectorType] = (out[c.connectorType] ?? 0) + 1;
    return out;
  }, [connectors]);

  const statusCounts = useMemo(() => {
    const out: Partial<Record<ConnectorStatus, number>> = {};
    for (const c of connectors) {
      const s = (c.status ?? 'disabled') as ConnectorStatus;
      out[s] = (out[s] ?? 0) + 1;
    }
    return out;
  }, [connectors]);

  const handleDelete = useCallback(
    async (c: Connector) => {
      if (!confirm(`Delete connector "${c.name}"? This cannot be undone.`)) return;
      try {
        setPendingId(rowKey(c));
        await deleteConnector.mutateAsync({ configId: c.id! });
        toast({ title: 'Deleted', description: `"${c.name}" removed.` });
        queryClient.invalidateQueries({ queryKey: ['connectors'] });
      } catch (err) {
        toast({
          title: 'Delete failed',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      } finally {
        setPendingId(null);
      }
    },
    [deleteConnector, queryClient, toast]
  );

  const handleRunNow = useCallback(
    async (c: Connector) => {
      try {
        setPendingId(rowKey(c));
        await triggerExtraction.mutateAsync({
          connectorType: c.connectorType,
          configurationId: c.id ? String(c.id) : undefined,
        });
        toast({ title: 'Triggered', description: `Run started for "${c.name}".` });
      } catch (err) {
        toast({
          title: 'Run failed',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      } finally {
        setPendingId(null);
      }
    },
    [triggerExtraction, toast]
  );

  const handleTest = useCallback(
    async (c: Connector) => {
      try {
        setPendingId(rowKey(c));
        await healthCheck.mutateAsync(c.connectorType);
        toast({ title: 'Connection OK', description: `"${c.name}" health check passed.` });
      } catch (err) {
        toast({
          title: 'Connection failed',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      } finally {
        setPendingId(null);
      }
    },
    [healthCheck, toast]
  );

  const onFilterByStatus = (status: ConnectorStatus | null) => {
    if (status === null) lib.setStatuses([]);
    else lib.setStatuses([status]);
  };

  /**
   * Map TanStack sort changes back to the URL via `useConnectorLibraryState`,
   * through the router so the page resets to 1 and the Connectors back link
   * (which replays the last library URL) keeps the sort (#2161).
   */
  const { setSort, setPage } = lib;
  const handleSortingChange = useCallback(
    (next: SortingState) => {
      const first = next[0];
      setSort(first ? { column: first.id as SortColumn, direction: first.desc ? 'desc' : 'asc' } : null);
    },
    [setSort]
  );

  /**
   * The page lives in the URL (`?page=`) and the page size in the user's
   * preferences, so a background poll, Run Now or a trip to a connector and
   * back cannot lose it (#2161). A URL page past the end (rows were filtered
   * or deleted away) shows the last page without rewriting the URL.
   */
  const pagination = useMemo<PaginationState>(() => {
    const lastPageIndex = Math.max(Math.ceil(filtered.length / pageSize) - 1, 0);
    return { pageIndex: Math.min(lib.page - 1, lastPageIndex), pageSize };
  }, [filtered.length, lib.page, pageSize]);

  const handlePaginationChange = useCallback(
    (next: PaginationState) => {
      if (next.pageSize !== pageSize) setPageSize(next.pageSize);
      setPage(next.pageIndex + 1);
    },
    [pageSize, setPageSize, setPage]
  );

  const initialSorting: SortingState = useMemo(
    () => [{ id: lib.sort.column, desc: lib.sort.direction === 'desc' }],
    // Initial only — re-read URL on mount; user-driven changes flow through
    // handleSortingChange. (Listing lib.sort here would reset state every
    // time the URL changed.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const columns = useMemo<ColumnDef<Connector>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <Checkbox
            aria-label="Select all visible connectors"
            checked={
              selection.isAllVisibleSelected
                ? true
                : selection.isSomeVisibleSelected
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={() => selection.toggleAllVisible()}
          />
        ),
        cell: ({ row }) => {
          const c = row.original;
          const id = rowKey(c);
          return (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                aria-label={`Select ${c.name}`}
                checked={!!selection.rowSelection[id]}
                onCheckedChange={() => selection.toggleRow(id)}
              />
              <BulkRowStatusBadge status={bulk.rowStatus[id] ?? 'idle'} />
            </div>
          );
        },
      },
      {
        id: 'name',
        accessorFn: (c) => c.name,
        header: ({ column }) => <DataTableSortHeader column={column}>Connector</DataTableSortHeader>,
        cell: ({ row }) => {
          const c = row.original;
          const meta = getPlatformMeta(c.connectorType);
          const unnamed = isUnnamed(c);
          return (
            <div className="flex items-center gap-3 whitespace-nowrap">
              <PlatformLogo connectorType={c.connectorType} size={28} />
              <div className="min-w-0">
                <div className="font-semibold leading-tight">
                  {unnamed ? (
                    <span
                      className="inline-flex items-center gap-1 text-warning"
                      title="This connector has no name — it cannot be opened until it is renamed."
                    >
                      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                      Unnamed connector
                    </span>
                  ) : (
                    highlightMatch(c.name, lib.q)
                  )}
                </div>
                <div className="text-xs text-muted-foreground leading-tight">
                  {highlightMatch(meta.displayName, lib.q)}
                  {c.id != null && (
                    <>
                      <span aria-hidden> · </span>
                      {/* Selectable for copying; must not trigger the row's navigate-on-click. */}
                      <span
                        data-testid="connector-id"
                        className="select-text font-mono"
                        onClick={(e) => e.stopPropagation()}
                      >
                        ID {c.id}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: 'status',
        accessorFn: (c) => c.status ?? '',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Status</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const status = (row.original.status ?? 'disabled') as keyof typeof STATUS_PILL;
          const pill = STATUS_PILL[status] ?? STATUS_PILL.disabled;
          return (
            <Badge variant="outline" className={`${pill.className} text-xs whitespace-nowrap`}>
              ● {pill.label}
            </Badge>
          );
        },
      },
      {
        id: 'last_run',
        accessorFn: (c) => lastRunSortKey(c),
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Last run</DataTableSortHeader>
        ),
        cell: ({ row }) => (
          <LastRunCell execution={row.original.latest_execution as any} />
        ),
        sortingFn: (a, b) => lastRunSortKey(a.original) - lastRunSortKey(b.original),
      },
      {
        id: 'next_run',
        accessorFn: (c) =>
          c.nextScheduledRun ? new Date(c.nextScheduledRun).getTime() : Number.MAX_SAFE_INTEGER,
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Next run</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const c = row.original;
          const cron = c.config?.schedule_cron ?? c.config?.schedule;
          const enabled = c.scheduleEnabled ?? c.config?.schedule_enabled ?? c.enabled ?? false;
          return (
            <NextRunCell
              nextRunAt={c.nextScheduledRun}
              cron={cron}
              enabled={enabled}
              tz={c.config?.schedule_timezone}
            />
          );
        },
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="text-right" onClick={(e) => e.stopPropagation()}>
              <RowActionsMenu
                isLoading={pendingId === rowKey(c)}
                onRunNow={() => handleRunNow(c)}
                onConfigure={() => onOpenConnector(c)}
                onViewHistory={() => onOpenConnector(c)}
                onTestConnection={() => handleTest(c)}
                onDelete={() => handleDelete(c)}
              />
            </div>
          );
        },
      },
    ],
    [lib.q, pendingId, handleRunNow, handleTest, handleDelete, onOpenConnector, selection, bulk.rowStatus]
  );

  function deselectSucceeded(succeeded: Connector[]): void {
    const succeededIds = new Set(succeeded.map((c) => rowKey(c)));
    const next = { ...selection.rowSelection };
    for (const id of succeededIds) delete next[id];
    selection.setRowSelection(next);
  }

  function summarizeToast(
    verb: 'Run' | 'Disable' | 'Delete',
    succeeded: Connector[],
    failed: Array<{ connector: Connector; error: string }>
  ): void {
    const total = succeeded.length + failed.length;
    const verbPast = verb === 'Run' ? 'started' : verb === 'Disable' ? 'disabled' : 'deleted';
    if (failed.length === 0) {
      toast({
        title: `${succeeded.length} of ${total} ${verbPast}`,
        description: succeeded.length === total ? 'All actions completed.' : '',
      });
      return;
    }
    const names = failed.slice(0, 3).map((f) => f.connector.name).join(', ');
    const more = failed.length > 3 ? ` and ${failed.length - 3} more` : '';
    toast({
      title: `${succeeded.length} of ${total} ${verbPast}`,
      description: `Failed: ${names}${more}`,
      variant: 'destructive',
    });
  }

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex items-center justify-between">
        <AggregateHealthBar
          total={connectors.length}
          counts={statusCounts}
          activeStatuses={lib.statuses}
          onFilterByStatus={onFilterByStatus}
        />
      </div>

      {/* Search + filter bar */}
      <div className="flex gap-2 items-center">
        <ConnectorSearchInput value={lib.q} onChange={lib.setQ} matchCount={filtered.length} />
        <ConnectorFilters
          platforms={lib.platforms}
          statuses={lib.statuses}
          onTogglePlatform={lib.togglePlatform}
          onToggleStatus={lib.toggleStatus}
          onClearAll={lib.clearAll}
          platformCounts={platformCounts}
          statusCounts={statusCounts}
        />
      </div>

      <ActiveFilterChips
        platforms={lib.platforms}
        statuses={lib.statuses}
        onRemovePlatform={lib.togglePlatform}
        onRemoveStatus={lib.toggleStatus}
        onClearAll={lib.clearAll}
      />

      {/* Body */}
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load connectors: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : connectors.length === 0 ? (
        <EmptyLibraryState
          variant="no-connectors"
          onAdd={onAddConnector ?? (() => {})}
          onClear={lib.clearAll}
        />
      ) : filtered.length === 0 ? (
        <EmptyLibraryState
          variant="no-matches"
          onAdd={onAddConnector ?? (() => {})}
          onClear={lib.clearAll}
        />
      ) : (
        <>
          {selection.count > 0 ? (
            <BulkActionBar
              count={selection.count}
              enabledCount={
                selection.selectedConnectors(connectors).filter((c) => c.enabled !== false).length
              }
              isAnyPending={bulk.isAnyPending}
              onRun={async () => {
                const selected = selection.selectedConnectors(connectors);
                const { succeeded, failed } = await bulk.runBulk(selected);
                deselectSucceeded(succeeded);
                summarizeToast('Run', succeeded, failed);
              }}
              onDisable={() => setConfirmDisable(true)}
              onDelete={() => setConfirmDelete(true)}
              onClear={() => {
                selection.clear();
                bulk.clearStatus();
              }}
            />
          ) : null}
          <DataTable
            data={filtered}
            columns={columns}
            initialSorting={initialSorting}
            onSortingChange={handleSortingChange}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            autoResetPageIndex={false}
            rowSelection={selection.rowSelection}
            onRowSelectionChange={(s) => selection.setRowSelection(s)}
            onRowClick={(c) => {
              // #970: an unnamed row has no valid (type, name) route — never
              // navigate, or it would collide with / misroute to another row.
              if (isUnnamed(c)) return;
              navigate(`/connectors/${c.connectorType}/${encodeURIComponent(c.name)}?tab=activity`);
            }}
            getRowId={(c) => rowKey(c)}
            rowProps={(c) => {
              const brand = getPlatformMeta(c.connectorType).bg;
              const baseStyle: React.CSSProperties = {
                boxShadow: `inset 3px 0 0 0 ${brand}`,
              };
              if (c.status === 'running') {
                return {
                  'data-state': 'running',
                  'data-platform': c.connectorType,
                  style: { ...baseStyle, background: 'rgba(255, 251, 235, 0.6)' },
                };
              }
              return {
                'data-state': c.status ?? 'disabled',
                'data-platform': c.connectorType,
                style: baseStyle,
              };
            }}
          />

          <BulkConfirmDialog
            open={confirmDisable}
            title={`Disable ${selection.selectedConnectors(connectors).filter((c) => c.enabled !== false).length} ${
              selection.selectedConnectors(connectors).filter((c) => c.enabled !== false).length === 1 ? 'connector' : 'connectors'
            }?`}
            description="These connectors will be disabled and stop running on their schedule:"
            names={selection
              .selectedConnectors(connectors)
              .filter((c) => c.enabled !== false)
              .map((c) => c.name)}
            skipCount={
              selection.selectedConnectors(connectors).filter((c) => c.enabled === false).length
            }
            confirmLabel={`Disable ${selection.selectedConnectors(connectors).filter((c) => c.enabled !== false).length} ${
              selection.selectedConnectors(connectors).filter((c) => c.enabled !== false).length === 1 ? 'connector' : 'connectors'
            }`}
            onCancel={() => setConfirmDisable(false)}
            onConfirm={async () => {
              setConfirmDisable(false);
              const selected = selection.selectedConnectors(connectors);
              const { succeeded, failed } = await bulk.disableBulk(selected);
              deselectSucceeded(succeeded);
              summarizeToast('Disable', succeeded, failed);
            }}
          />

          <BulkDeleteDialog
            open={confirmDelete}
            names={selection.selectedConnectors(connectors).map((c) => c.name)}
            onCancel={() => setConfirmDelete(false)}
            onConfirm={async () => {
              setConfirmDelete(false);
              const selected = selection.selectedConnectors(connectors);
              const { succeeded, failed } = await bulk.deleteBulk(selected);
              deselectSucceeded(succeeded);
              summarizeToast('Delete', succeeded, failed);
            }}
          />
        </>

      )}
    </div>
  );
};

// Re-export SortColumn type so existing imports continue to work.
export type { SortColumn };
